import React, { useEffect, useState } from 'react';
import { Clock, MapPin, Phone, Mail, Car } from 'lucide-react';

function ComingSoonPage() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#0A4D8C] via-[#083A6B] to-[#062847]">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0 animate-pulse" 
          style={{
            backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Floating Shapes Animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '0s', animationDuration: '8s' }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl animate-float-delayed"
          style={{ animationDelay: '2s', animationDuration: '10s' }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <div className={`max-w-4xl w-full space-y-12 text-center transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* Logo / Icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-white/20 rounded-full blur-2xl animate-pulse" />
              <div className="relative bg-white/10 backdrop-blur-sm p-8 rounded-full border-2 border-white/20">
                <Car className="w-16 h-16 text-white animate-bounce-slow" />
              </div>
            </div>
          </div>

          {/* Main Heading */}
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-4">
              <Clock className="w-4 h-4 text-white animate-spin-slow" />
              <span className="text-sm font-medium text-white/90">Kemur fljótlega</span>
            </div>
            
            <h1 
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Vefurinn okkar er
              <span className="block bg-gradient-to-r from-cyan-300 via-blue-300 to-blue-400 bg-clip-text text-transparent animate-gradient">
                í smíðum
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto leading-relaxed">
              Við erum að vinna í að skapa betri upplifun fyrir þig. Í millitíðinni getur þú haft samband við okkur með eftirfarandi hætti.
            </p>
          </div>

          {/* Contact Information Cards */}
          <div className="grid md:grid-cols-2 gap-6 mt-16 max-w-3xl mx-auto">
            {/* Phone Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
              <div className="relative bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105">
                <div className="inline-flex p-3 bg-white/10 rounded-xl mb-4">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Símanúmer</h3>
                <a 
                  href="tel:5880705" 
                  className="text-2xl font-bold text-white hover:text-cyan-300 transition-colors duration-200"
                >
                  588-0705
                </a>
                <p className="text-sm text-white/60 mt-2">Opið virka daga 08:30-18:00</p>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-white/60 mb-1">Neyðarsími (utan opnunartíma)</p>
                  <a 
                    href="tel:7835000" 
                    className="text-lg font-semibold text-cyan-300 hover:text-cyan-200 transition-colors duration-200"
                  >
                    783-5000
                  </a>
                </div>
              </div>
            </div>

            {/* Email Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
              <div className="relative bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105">
                <div className="inline-flex p-3 bg-white/10 rounded-xl mb-4">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Tölvupóstur</h3>
                <a 
                  href="mailto:alvorubon@alvorubon.is" 
                  className="text-xl font-bold text-white hover:text-cyan-300 transition-colors duration-200 break-all"
                >
                  alvorubon@alvorubon.is
                </a>
              </div>
            </div>
          </div>

          {/* Address Card - Full Width */}
          <div className="group relative max-w-3xl mx-auto mt-8">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
            <div className="relative bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-[1.02]">
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                <div className="inline-flex p-3 bg-white/10 rounded-xl">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-lg font-semibold text-white mb-1">Staðsetning</h3>
                  <p className="text-2xl font-bold text-white">Viðarhöfða 2a</p>
                  <p className="text-xl text-white/80">110 Reykjavík</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-16 pt-8 border-t border-white/10">
            <p className="text-white/60 text-sm">
              © 2025 Alvörubón ehf. Öll réttindi áskilin.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        @keyframes float-delayed {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(-30px, 30px) scale(0.9);
          }
          66% {
            transform: translate(20px, -20px) scale(1.1);
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-float {
          animation: float 8s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 4s ease infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default ComingSoonPage;
